<template>
  <div>
    <div class="config-panel-header">
      <h2 class="text-h5 font-weight-bold mb-4" :style="{ color: $getColor('vuetifyColor1') }">
        <v-icon :color="$getColor('vuetifyColor1')" large class="mr-2">mdi-cog</v-icon>
        Configuración del Sistema
      </h2>
      <p class="text-subtitle-1 text--secondary">
        Panel exclusivo para administradores del sistema. Aquí podrás ir añadiendo componentes de configuración globales.
      </p>
    </div>

    <v-divider class="mb-6"></v-divider>

    <!-- Config Tabs -->
    <v-tabs
      v-model="activeTab"
      background-color="transparent"
      color="primary"
      class="mb-6 dashboard_box"
      show-arrows
    >
      <v-tab v-for="category in categoryOptions" :key="category.value">
        <v-icon left v-if="category.icon">{{ category.icon }}</v-icon>
        {{ category.text }}
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="activeTab" style="background-color: transparent;">
      <!-- Appearance Tab -->
      <v-tab-item>
        <div class="config-panel-grid align-start" style="align-items: start;">
          <!-- Appearance Configuration -->
          <mdtec-panel>
            <template v-slot:title> Apariencia y Tipografía </template>
            <template v-slot:content>
              <div class="pa-4">
                <p class="text-body-2 text--secondary mb-4">
                  Selecciona la familia tipográfica que se aplicará a toda la plataforma.
                  El cambio es persistente en tu navegador y se aplica de inmediato.
                </p>
                <v-select
                  v-model="selectedTypography"
                  :items="typographyOptions"
                  label="Tipografía Global"
                  outlined
                  dense
                  hide-details
                  @change="updateTypography"
                ></v-select>
              </div>
            </template>
          </mdtec-panel>

          <!-- Theme Mode Configuration -->
          <mdtec-panel>
            <template v-slot:title> Tema Predeterminado (Oscuro/Claro) </template>
            <template v-slot:content>
              <div class="pa-4">
                <p class="text-body-2 text--secondary mb-4">
                  Selecciona el modo visual predeterminado para el panel de administración. Esto cambiará de inmediato la paleta de colores general al Tema Oscuro o Claro de la plataforma.
                </p>
                <div class="d-flex justify-center mt-2">
                 <v-btn-toggle
                   v-model="darkThemeGlobal"
                   mandatory
                   rounded
                   color="primary"
                   class="mb-2"
                 >
                   <v-btn :value="false" min-width="120">
                     <v-icon left>mdi-white-balance-sunny</v-icon>
                     Claro
                   </v-btn>
                   <v-btn :value="true" min-width="120">
                     <v-icon left>mdi-weather-night</v-icon>
                     Oscuro
                   </v-btn>
                 </v-btn-toggle>
                </div>
              </div>
            </template>
          </mdtec-panel>
        </div>
      </v-tab-item>

      <!-- Colors Tab -->
      <v-tab-item>
        <div class="config-panel-grid" style="align-items: start;">
          <!-- Color Configuration -->
          <mdtec-panel>
            <template v-slot:title> Color Corporativo </template>
            <template v-slot:content>
              <div class="pa-4 flex-column align-center d-flex">
                <p class="text-body-2 text--secondary mb-4 text-center">
                  Ajusta el color principal utilizado en botones y elementos destacados.
                </p>
                <v-color-picker
                  v-model="selectedColor"
                  @update:color="updateColor"
                  mode="hexa"
                  hide-mode-switch
                  hide-inputs
                  show-swatches
                  :swatches="predefinedColors"
                  elevation="0"
                  class="mb-2"
                ></v-color-picker>
              </div>
            </template>
          </mdtec-panel>

          <!-- Secondary Color Configuration -->
          <mdtec-panel>
            <template v-slot:title> Color Secundario </template>
            <template v-slot:content>
              <div class="pa-4 flex-column align-center d-flex">
                <p class="text-body-2 text--secondary mb-4 text-center">
                  Ajusta el color empleado en botones secundarios y resaltes de éxito.
                </p>
                <v-color-picker
                  v-model="selectedSecondaryColor"
                  @update:color="updateSecondaryColor"
                  mode="hexa"
                  hide-mode-switch
                  hide-inputs
                  show-swatches
                  :swatches="predefinedSecondaryColors"
                  elevation="0"
                  class="mb-2"
                ></v-color-picker>
              </div>
            </template>
          </mdtec-panel>
        </div>
      </v-tab-item>
      <!-- Backgrounds & Nav Tab -->
      <v-tab-item>
        <div class="config-panel-grid align-start" style="align-items: start;">
          <!-- Light Theme Background Color -->
          <mdtec-panel v-if="!darkThemeGlobal">
            <template v-slot:title> Color de Fondo (Modo Claro) </template>
            <template v-slot:content>
              <div class="pa-4 flex-column align-center d-flex">
                <p class="text-body-2 text--secondary mb-4 text-center">
                  Ajusta el color del fondo principal cuando el Tema Claro está activo.
                </p>
                <v-color-picker
                  v-model="lightThemeColorGlobal"
                  mode="hexa"
                  hide-mode-switch
                  hide-inputs
                  show-swatches
                  :swatches="predefinedLightColors"
                  elevation="0"
                  class="mb-2"
                ></v-color-picker>
              </div>
            </template>
          </mdtec-panel>
          <!-- Dark Theme Background Color (repeat here for convenience) -->
          <mdtec-panel v-if="darkThemeGlobal">
            <template v-slot:title> Color de Fondo (Modo Oscuro) </template>
            <template v-slot:content>
              <div class="pa-4 flex-column align-center d-flex">
                <p class="text-body-2 text--secondary mb-4 text-center">
                  Ajusta el color del fondo principal cuando el Tema Oscuro está activo.
                </p>
                <v-color-picker
                  v-model="darkThemeColorGlobal"
                  mode="hexa"
                  hide-mode-switch
                  hide-inputs
                  show-swatches
                  :swatches="predefinedDarkColors"
                  elevation="0"
                  class="mb-2"
                ></v-color-picker>
              </div>
            </template>
          </mdtec-panel>
          <!-- Nav Bar Color -->
          <mdtec-panel>
            <template v-slot:title> Color de la Barra de Navegación (Logo) </template>
            <template v-slot:content>
              <div class="pa-4 flex-column align-center d-flex">
                <p class="text-body-2 text--secondary mb-4 text-center">
                  Ajusta el color de la barra superior donde aparece el logo de la empresa.
                </p>
                <v-color-picker
                  v-model="navColorGlobal"
                  mode="hexa"
                  hide-mode-switch
                  hide-inputs
                  show-swatches
                  :swatches="predefinedNavColors"
                  elevation="0"
                  class="mb-2"
                ></v-color-picker>
                <v-btn
                  small
                  text
                  class="mt-1"
                  @click="$store.commit('setNavColor', '')"
                >
                  <v-icon left small>mdi-restore</v-icon>
                  Restablecer por defecto
                </v-btn>
              </div>
            </template>
          </mdtec-panel>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import MdtecPanel from '@/components/Cards/MdtecPanel.vue';

export default {
  name: 'AdminConfigUI',
  components: {
    MdtecPanel,
  },
  data() {
    return {
      activeTab: 0,
      categoryOptions: [
        { text: 'Apariencia y Tipografía', value: 'appearance', icon: 'mdi-format-font' },
        { text: 'Colores Corporativos', value: 'colors', icon: 'mdi-palette' },
        { text: 'Fondos y Barra de Nav', value: 'backgrounds', icon: 'mdi-image-filter-hdr' },
      ],
      selectedTypography: this.$store.state.typographyClass,
      typographyOptions: [
        { text: 'Roboto (Sistema / Base)', value: 'typography-opcion1' },
        { text: 'Montserrat', value: 'typography-opcion2' },
        { text: 'Poppins', value: 'typography-opcion3' },
        { text: 'Lato', value: 'typography-opcion4' },
        { text: 'Inter (Recomendada)', value: 'typography-opcion5' },
        { text: 'Space Grotesk (Radical)', value: 'typography-opcion6' },
      ],
      selectedColor: this.$store.state.primaryColor,
      predefinedColors: [
        ['#F4796B', '#FF5722', '#F44336'],
        ['#E91E63', '#9C27B0', '#673AB7'],
        ['#3F51B5', '#2196F3', '#03A9F4'],
        ['#00BCD4', '#009688', '#4CAF50'],
        ['#8BC34A', '#CDDC39', '#FFEB3B'],
        ['#FFC107', '#FF9800', '#795548'],
      ],
      selectedSecondaryColor: this.$store.state.secondaryColor,
      predefinedSecondaryColors: [
        ['#00BFA5', '#4CAF50', '#8BC34A'],
        ['#00BCD4', '#03A9F4', '#009688'],
        ['#FFC107', '#FF9800', '#FF5722'],
        ['#E91E63', '#9C27B0', '#673AB7'],
        ['#3F51B5', '#2196F3', '#607D8B'],
        ['#795548', '#9E9E9E', '#607D8B'],
      ],
      predefinedDarkColors: [
        ['#121212', '#1E1E1E', '#2A2A2A'],
        ['#0A192F', '#112240', '#233554'], // Navy darks
        ['#1A202C', '#2D3748', '#4A5568'], // Blue-greys
        ['#18181B', '#27272A', '#3F3F46'], // Zinc darks
        ['#0F172A', '#1E293B', '#334155'], // Slate darks
      ],
      predefinedLightColors: [
        ['#F9F9FF', '#F5F5F5', '#EEEEEE'],
        ['#FFF8F0', '#FFF3E0', '#FBE9E7'], // Warm whites
        ['#F0F4FF', '#E8F0FE', '#E3F2FD'], // Cool blues
        ['#F0FFF4', '#E8F5E9', '#F1F8E9'], // Greens
        ['#FFF0F5', '#FCE4EC', '#F3E5F5'], // Purples/pinks
      ],
      predefinedNavColors: [
        ['#FFFFFF', '#F5F5F5', '#FAFAFA'],
        ['#1976D2', '#1565C0', '#0D47A1'], // Blues
        ['#212121', '#424242', '#616161'], // Darks
        ['#F4796B', '#FF5722', '#E91E63'], // Reds/pinks
        ['#2E7D32', '#388E3C', '#00695C'], // Greens
      ],
    };
  },
  computed: {
    darkThemeGlobal: {
      get() {
        return this.$store.state.darkTheme;
      },
      set(value) {
        this.$store.commit('setDarkTheme', value);
      },
    },
    darkThemeColorGlobal: {
      get() {
        return this.$store.state.darkThemeColor;
      },
      set(value) {
        const hexColor = typeof value === 'object' ? value.hex : value;
        this.$store.commit('setDarkThemeColor', hexColor);
      },
    },
    lightThemeColorGlobal: {
      get() {
        return this.$store.state.lightThemeColor;
      },
      set(value) {
        const hexColor = typeof value === 'object' ? value.hex : value;
        this.$store.commit('setLightThemeColor', hexColor);
      },
    },
    navColorGlobal: {
      get() {
        return this.$store.state.navColor || '#FFFFFF';
      },
      set(value) {
        const hexColor = typeof value === 'object' ? value.hex : value;
        this.$store.commit('setNavColor', hexColor);
      },
    },
  },
  methods: {
    updateTypography(val) {
      this.$store.commit('setTypographyClass', val);
    },
    updateColor(val) {
      const hexColor = typeof val === 'object' ? val.hex : val;
      this.$store.commit('setPrimaryColor', hexColor);
    },
    updateSecondaryColor(val) {
      const hexColor = typeof val === 'object' ? val.hex : val;
      this.$store.commit('setSecondaryColor', hexColor);
    },
  },
};
</script>

<style lang="scss" scoped>
.config-panel-header {
  margin-bottom: calc(var(--base) * 2);
}

.config-panel-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: calc(var(--base) * 3);

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
